import type {
  LaunchCandidate,
  LaunchCandidateDismissAllBody,
  LaunchCandidateListResponse,
  LaunchCandidateReview,
  LaunchCandidateReviewResult,
  LaunchCandidateState,
  LaunchSyncResult,
  NewLaunchCandidateBody,
  RecordingUpload,
  ReleaseEntry,
  ReleaseEntryInput,
  ReleaseEntryListResponse,
  ReleaseEntryPatch,
} from "../../models/releases";

import { getApiClient } from "@punchup/utils/apiRegistry";

const RELEASES_API = "/api/v2/internal/releases";

const entryPath = (id: string) => `${RELEASES_API}/${encodeURIComponent(id)}`;

/** Every entry, drafts included (super-admin). */
export async function fetchReleaseEntries(): Promise<ReleaseEntryListResponse> {
  const response = await getApiClient().get<{
    data: ReleaseEntryListResponse;
  }>(RELEASES_API);
  return response.data;
}

export async function postReleaseEntry(
  input: ReleaseEntryInput
): Promise<ReleaseEntry> {
  const response = await getApiClient().post<{ data: ReleaseEntry }>(
    RELEASES_API,
    input
  );
  return response.data;
}

export async function patchReleaseEntry(
  id: string,
  patch: ReleaseEntryPatch
): Promise<ReleaseEntry> {
  const response = await getApiClient().patch<{ data: ReleaseEntry }>(
    entryPath(id),
    patch
  );
  return response.data;
}

export async function removeReleaseEntry(id: string): Promise<void> {
  await getApiClient().del(entryPath(id));
}

/** Adds the sample board to the table (idempotent) and returns the whole list. */
export async function loadSampleReleaseEntries(): Promise<ReleaseEntryListResponse> {
  const response = await getApiClient().post<{
    data: ReleaseEntryListResponse;
  }>(`${RELEASES_API}/sample`);
  return response.data;
}

// ---------------------------------------------------------------------------
// The launch hub inbox
// ---------------------------------------------------------------------------

const CANDIDATES_API = `${RELEASES_API}/candidates`;

/** One review state of the inbox, newest source first (super-admin). */
export async function fetchLaunchCandidates(
  state: LaunchCandidateState
): Promise<LaunchCandidateListResponse> {
  const response = await getApiClient().get<{
    data: LaunchCandidateListResponse;
  }>(`${CANDIDATES_API}?state=${encodeURIComponent(state)}`);
  return response.data;
}

/** Pastes a post into the inbox; the server drafts it and returns the card. */
export async function postLaunchCandidate(
  body: NewLaunchCandidateBody
): Promise<LaunchCandidate> {
  const response = await getApiClient().post<{ data: LaunchCandidate }>(
    CANDIDATES_API,
    body
  );
  return response.data;
}

export async function reviewLaunchCandidate(
  id: string,
  review: LaunchCandidateReview
): Promise<LaunchCandidateReviewResult> {
  const response = await getApiClient().post<{
    data: LaunchCandidateReviewResult;
  }>(`${CANDIDATES_API}/${encodeURIComponent(id)}/review`, review);
  return response.data;
}

/** "Upload a recording": a one-time Stream upload URL and the GIF the recording becomes. */
export async function startRecordingUpload(): Promise<RecordingUpload> {
  const response = await getApiClient().post<{ data: RecordingUpload }>(
    `${RELEASES_API}/recording-upload`
  );
  return response.data;
}

/** "Dismiss all": clears many pending cards with one reason; returns how many went. */
export async function dismissLaunchCandidates(
  body: LaunchCandidateDismissAllBody
): Promise<{ dismissed: number }> {
  const response = await getApiClient().post<{ data: { dismissed: number } }>(
    `${CANDIDATES_API}/dismiss`,
    body
  );
  return response.data;
}

/** "Pull from Slack": adds new #product-launches posts to the inbox as drafted cards. */
export async function syncLaunchCandidatesFromSlack(): Promise<LaunchSyncResult> {
  const response = await getApiClient().post<{ data: LaunchSyncResult }>(
    `${CANDIDATES_API}/sync`
  );
  return response.data;
}
